import { onMounted, onUnmounted, watch } from 'vue'
import { useGoogleAuth } from '@/composables/useGoogleAuth'
import {
  BOOKMARKS_CHANGED_EVENT,
  BOOKMARKS_SYNCED_EVENT,
  getBookmarksUpdatedAt,
  loadAyahBookmarks,
  saveAyahBookmarks,
} from '@/lib/bookmarks'
import { fetchRemoteBookmarks, mergeBookmarks, pushRemoteBookmarks } from '@/lib/bookmarksSync'

/**
 * Keeps local bookmarks in sync with the signed-in Google account's cloud copy.
 * - On sign-in / load: reconciles local and remote (last-write-wins when both are
 *   timestamped; union on the first/legacy sync so nothing is lost).
 * - On local changes: debounced push to the server.
 * Degrades silently to local-only when signed out or the backend isn't configured.
 *
 * Mount this once (in the dashboard layout).
 */
export function useBookmarksSync() {
  const { isSignedIn } = useGoogleAuth()

  let pushTimer: ReturnType<typeof setTimeout> | undefined
  let reconciling = false

  const reconcile = async () => {
    if (!isSignedIn.value || reconciling) return
    reconciling = true
    try {
      const remote = await fetchRemoteBookmarks()
      if (remote === 'unavailable' || remote === null) return

      const local = loadAyahBookmarks()
      const localUpdatedAt = getBookmarksUpdatedAt()
      const remoteTime = remote.updatedAt ? Date.parse(remote.updatedAt) : 0
      const localTime = localUpdatedAt ? Date.parse(localUpdatedAt) : 0

      if (remote.updatedAt && localUpdatedAt) {
        // Both sides have synced before → newest wins (preserves deletions).
        if (remoteTime > localTime) {
          saveAyahBookmarks(remote.items, { updatedAt: remote.updatedAt, silent: true })
          window.dispatchEvent(new CustomEvent(BOOKMARKS_SYNCED_EVENT))
        } else if (localTime > remoteTime) {
          await pushRemoteBookmarks(local, localUpdatedAt)
        }
        return
      }

      // First sync (or legacy local without a timestamp) → union to avoid data loss.
      const merged = mergeBookmarks(local, remote.items)
      const ts = new Date().toISOString()
      saveAyahBookmarks(merged, { updatedAt: ts, silent: true })
      window.dispatchEvent(new CustomEvent(BOOKMARKS_SYNCED_EVENT))
      await pushRemoteBookmarks(merged, ts)
    } finally {
      reconciling = false
    }
  }

  const schedulePush = () => {
    if (!isSignedIn.value) return
    if (pushTimer) clearTimeout(pushTimer)
    pushTimer = setTimeout(() => {
      void pushRemoteBookmarks(loadAyahBookmarks(), getBookmarksUpdatedAt() || new Date().toISOString())
    }, 1200)
  }

  const onLocalChange = () => schedulePush()

  onMounted(() => {
    window.addEventListener(BOOKMARKS_CHANGED_EVENT, onLocalChange)
    if (isSignedIn.value) void reconcile()
  })

  onUnmounted(() => {
    window.removeEventListener(BOOKMARKS_CHANGED_EVENT, onLocalChange)
    if (pushTimer) clearTimeout(pushTimer)
  })

  watch(isSignedIn, (signedIn) => {
    if (signedIn) void reconcile()
  })

  return { reconcile }
}
